import { useCallback, useEffect, useRef, useState } from 'react';
import {
    FITTED_VIEW,
    FIT_SCALE,
    clampView,
    doubleTapScale,
    isFitted,
    stepView,
} from './zoomFrame';
import { usePictureGesture } from './usePictureGesture';

/**
 * Where one picture in the full-screen viewer sits, and every way of moving it
 * that is not a finger (SPEC-012 PR-12.2).
 *
 * The arithmetic is `zoomFrame.js`; the fingers are `usePictureGesture`; this is
 * the view itself — the one thing that outlives every gesture — plus the wheel,
 * the keys and the two commands the viewer's chrome aims at whichever take is
 * showing.
 *
 * It writes `style.transform`, not React state. The view lives in a ref and goes
 * straight to the element at the pointer's sampling rate. Exactly one bit reaches
 * React — whether this picture is fitted — because that bit changes a class and a
 * button's label, and re-rendering a dialog on every frame of a pinch is how a
 * 120Hz phone starts dropping them (SPEC-012 D-5).
 */

/** Wheel delta to scale, multiplicatively — so in and out are symmetric. */
const WHEEL_RATE = 0.0015;
const LINE_HEIGHT = 16;

/** What one press of `+`, `−` or an arrow key is worth. */
const KEY_ZOOM = 1.4;
const KEY_PAN = 64;

export function usePinchZoom({ railRef }) {
    const frameRef = useRef(null);
    const pictureRef = useRef(null);
    // Client coordinates, in the order the fingers landed: `pinchSpread` reads
    // the first two, and a Map is the only collection that promises that order.
    // Owned here rather than in the gesture hook because `apply` needs the count
    // — a second finger down is half of what makes the gesture ours.
    const pointersRef = useRef(new Map());
    const viewRef = useRef(FITTED_VIEW);
    const deferredResetRef = useRef(false);
    const [fitted, setFitted] = useState(true);

    /** The frame is one screen; the picture is what `contain` sizing made it. */
    const boxes = useCallback(() => ({
        frame: {
            width: frameRef.current?.clientWidth ?? 0,
            height: frameRef.current?.clientHeight ?? 0,
        },
        // `offsetWidth` ignores transforms, so this stays the FITTED size no
        // matter how far the picture is currently zoomed — which is exactly the
        // number every pan bound is a multiple of (SPEC-012 D-1).
        fitted: {
            width: pictureRef.current?.offsetWidth ?? 0,
            height: pictureRef.current?.offsetHeight ?? 0,
        },
    }), []);

    const apply = useCallback((view, animate = false) => {
        const picture = pictureRef.current;
        const frame = frameRef.current;

        viewRef.current = view;

        if (picture) {
            picture.style.transform =
                `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`;
        }

        if (frame) {
            // Data attributes and not classes: the class on this element is
            // React's, and one attribute with one writer is how the two stay out
            // of each other's way. `seized` flips `touch-action` between the
            // rail's swipe and ours, so it has to be right before the next
            // pointer event rather than after the next render (SPEC-012 D-8).
            if (isFitted(view) && pointersRef.current.size < 2) {
                delete frame.dataset.seized;
            } else {
                frame.dataset.seized = 'true';
            }

            // A step the reader asked for in one go — a double tap, the button,
            // a key — travels; one they are dragging must not lag behind the
            // finger. Cleared by every un-animated call, which is every pointer
            // frame, so a transition can never be left switched on.
            if (animate) {
                frame.dataset.settling = 'true';
            } else {
                delete frame.dataset.settling;
            }
        }

        setFitted(isFitted(view));
    }, []);

    const settle = useCallback((view, animate) => {
        const { frame, fitted: size } = boxes();

        apply(clampView(view, frame, size), animate);
    }, [apply, boxes]);

    /** The centre of the frame, in client coordinates. */
    const frameOrigin = useCallback(() => {
        const box = frameRef.current.getBoundingClientRect();

        return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    }, []);

    /**
     * Zoom about a point given in client coordinates — a tap, a wheel, or the
     * middle of the frame for the button and the keys.
     */
    const zoomAbout = useCallback((scale, at, animate = true) => {
        const origin = frameOrigin();
        const anchor = at
            ? { x: at.x - origin.x, y: at.y - origin.y }
            : { x: 0, y: 0 };

        settle(stepView(viewRef.current, { scale, from: anchor, to: anchor }), animate);
    }, [frameOrigin, settle]);

    /**
     * Back to the whole picture — but never out from under a finger.
     *
     * The viewer re-fits every take that is not the one showing (SPEC-012 D-12),
     * and the gesture that CHANGES which take is showing is a drag past this
     * picture's own edge (D-7). So the naive version of this fires mid-drag, on
     * the very picture being dragged, and the reader watches it snap back to fit
     * with their finger still down. Deferred to the moment they lift it.
     */
    const reset = useCallback(() => {
        if (pointersRef.current.size > 0) {
            deferredResetRef.current = true;

            return;
        }

        apply(FITTED_VIEW);
    }, [apply]);

    const onIdle = useCallback(() => {
        if (!deferredResetRef.current) {
            return;
        }

        deferredResetRef.current = false;
        apply(FITTED_VIEW);
    }, [apply]);

    const toggleZoom = useCallback((at) => {
        const { frame, fitted: size } = boxes();

        zoomAbout(isFitted(viewRef.current) ? doubleTapScale(frame, size) : FIT_SCALE, at);
    }, [boxes, zoomAbout]);

    const onKeyDown = useCallback((event) => {
        const rail = railRef.current;
        const view = viewRef.current;

        // A fitted picture has nothing to pan, so its arrow keys are the rail's —
        // the paging a scroll container would have given for free, and which this
        // slide has to hand back explicitly because it is no longer one.
        if (isFitted(view) && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            event.preventDefault();
            rail?.scrollBy({
                left: event.key === 'ArrowLeft' ? -rail.clientWidth : rail.clientWidth,
                behavior: 'smooth',
            });

            return;
        }

        const pan = (x, y) => settle(stepView(view, { from: { x: 0, y: 0 }, to: { x, y } }), true);
        const moves = {
            ArrowLeft: () => pan(KEY_PAN, 0),
            ArrowRight: () => pan(-KEY_PAN, 0),
            ArrowUp: () => pan(0, KEY_PAN),
            ArrowDown: () => pan(0, -KEY_PAN),
            '+': () => zoomAbout(view.scale * KEY_ZOOM),
            '=': () => zoomAbout(view.scale * KEY_ZOOM),
            '-': () => zoomAbout(view.scale / KEY_ZOOM),
            0: () => reset(),
        };

        if (moves[event.key]) {
            event.preventDefault();
            moves[event.key]();
        }
    }, [railRef, reset, settle, zoomAbout]);

    // Bound natively, and not through React's `onWheel`: React attaches wheel
    // listeners to the root as PASSIVE, and a passive listener cannot stop
    // ctrl+wheel from zooming the whole browser page out from under the dialog
    // (SPEC-012 D-9).
    useEffect(() => {
        const frame = frameRef.current;

        if (!frame) {
            return undefined;
        }

        const onWheel = (event) => {
            // A horizontal wheel is a trackpad's two-finger swipe, and on this
            // screen that means the next take. It belongs to the rail, so it is
            // left alone — ctrl+wheel excepted, which is a trackpad PINCH and
            // would otherwise zoom the whole browser page.
            if (!event.ctrlKey && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                return;
            }

            event.preventDefault();

            const delta = event.deltaMode === 1 ? event.deltaY * LINE_HEIGHT : event.deltaY;

            // Not animated: wheel ticks arrive in bursts, and a transition
            // chasing each one leaves the picture permanently behind the hand.
            zoomAbout(
                viewRef.current.scale * Math.exp(-delta * WHEEL_RATE),
                { x: event.clientX, y: event.clientY },
                false,
            );
        };

        frame.addEventListener('wheel', onWheel, { passive: false });

        return () => frame.removeEventListener('wheel', onWheel);
    }, [zoomAbout]);

    const pointerHandlers = usePictureGesture({
        frameRef,
        railRef,
        pointersRef,
        viewRef,
        boxes,
        apply,
        frameOrigin,
        toggleZoom,
        onIdle,
    });

    return {
        frameRef,
        pictureRef,
        fitted,
        reset,
        toggleZoom,
        handlers: { ...pointerHandlers, onKeyDown },
    };
}
